import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Edit, Trash2 } from 'lucide-react';
import { ConfrimModal } from '../../components/ConfrimModal';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import FbaTransferSidebar from './FbaTransferSidebar';
import FbaTransferStatusChip, { FBA_TRANSFER_STATUS_LABELS } from './FbaTransferStatusChip';
import { apiError, formatLongDate, formatMoney, formatQty } from './format';

const TH = 'px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide';

const FbaTransfers = ({ canEdit = false }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get('transfer');
  const [sidebar, setSidebar] = useState({ open: false, id: null });
  const [filters, setFilters] = useState({ status: '', sku: '', exceptionsOnly: false });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (openId) setSidebar({ open: true, id: openId });
  }, [openId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.sku.trim()) params.sku = filters.sku.trim();
      if (filters.exceptionsOnly) params.exceptionsOnly = 'true';
      const { data } = await axios.get('/api/v1/warehouse/fba-transfers', { params });
      setRows(data.rows || []);
    } catch (err) {
      if (!axios.isCancel(err)) setError(apiError(err, 'Failed to load transfers'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openTransfer = (id) => {
    setSidebar({ open: true, id });
    setParam('transfer', id ? String(id) : '');
  };
  const closeSidebar = () => {
    setSidebar({ open: false, id: null });
    setParam('transfer', '');
  };

  const deleteDraft = async () => {
    setDeleting(true);
    try {
      await axios.delete(`/api/v1/warehouse/fba-transfers/${toDelete.id}`);
      toast.success(`${toDelete.transfer_number} deleted`);
      if (String(sidebar.id) === String(toDelete.id)) closeSidebar();
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err, 'Failed to delete transfer'));
    } finally {
      setDeleting(false);
    }
  };

  const exceptionCount = rows.filter((row) => row.exceptions.length).length;

  return (
    <div className="flex h-[calc(100vh-48px)] min-h-[680px] flex-col overflow-hidden bg-white">
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2.5">
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-gray-900">Transfers to Amazon FBA</h1>
          <p className="text-xs text-gray-500">Create a transfer as a Draft, then Publish it to take the stock out of ShipHero. The shipment and labels are created in Seller Central, then its ID is attached here.</p>
        </div>
        {canEdit && <button type="button" onClick={() => openTransfer(null)} className="rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white">New Transfer</button>}
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select value={filters.status} onChange={(event) => setFilters((f) => ({ ...f, status: event.target.value }))} className="rounded-md border border-gray-300 px-2 py-1.5">
            <option value="">All statuses</option>
            {Object.entries(FBA_TRANSFER_STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <input value={filters.sku} onChange={(event) => setFilters((f) => ({ ...f, sku: event.target.value }))} placeholder="SKU" className="rounded-md border border-gray-300 px-2 py-1.5" />
          <label className="flex items-center gap-1"><input type="checkbox" checked={filters.exceptionsOnly} onChange={(event) => setFilters((f) => ({ ...f, exceptionsOnly: event.target.checked }))} /> Exceptions only</label>
          {!!exceptionCount && <span className="rounded-md bg-red-100 px-2 py-0.5 text-red-700">{exceptionCount} need attention</span>}
        </div>
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
        <div className="min-h-0 flex-1 overflow-auto rounded-lg border">
          <table className="min-w-full">
            <thead className="sticky top-0 bg-gray-50">
              <tr className="border-b border-gray-200">
                <th className={TH}>Transfer</th>
                <th className={TH}>Status</th>
                <th className={TH}>SKUs</th>
                <th className={`${TH} text-right`}>Units</th>
                <th className={`${TH} text-right`}>Total</th>
                <th className={TH}>Amazon Shipment</th>
                <th className={TH}><div className="flex items-center">Date Created <span className="ml-1">▾</span></div></th>
                <th className={TH}>Exceptions</th>
                {canEdit && <th className={TH}>Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={9} className="px-4 py-6 text-sm text-gray-500">Loading…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={9} className="px-4 py-6 text-sm text-gray-500">No transfers yet.</td></tr>}
              {!loading && rows.map((row) => {
                const isDraft = Boolean(row.deletable);
                return (
                  <tr key={row.id} onClick={() => openTransfer(row.id)} className="cursor-pointer hover:bg-gray-50">
                    <td className="whitespace-nowrap px-4 py-3 text-base font-bold text-gray-900">{row.transfer_number}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm"><FbaTransferStatusChip status={row.status} /></td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.lines.map((line) => line.supply_sku).filter((sku, i, all) => all.indexOf(sku) === i).join(', ')}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-900 tabular-nums">{formatQty(row.totals.qty_removed || row.totals.qty_planned)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{row.totals.extended_value ? formatMoney(row.totals.extended_value) : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{row.amazon_shipment_id || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{formatLongDate(row.created_at || row.createdAt)}</td>
                    <td className="px-4 py-3 text-sm text-red-700">{row.exceptions[0]?.message || ''}{row.exceptions.length > 1 ? ` (+${row.exceptions.length - 1})` : ''}</td>
                    {canEdit && (
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button type="button" aria-label="Open transfer" className="flex h-7 w-7 items-center justify-center rounded hover:bg-gray-100" onClick={(event) => { event.stopPropagation(); openTransfer(row.id); }}>
                            <Edit className="h-3.5 w-3.5 text-gray-900" />
                          </button>
                          <button
                            type="button"
                            aria-label="Delete transfer"
                            disabled={!isDraft}
                            title={isDraft ? 'Delete transfer' : 'Only Drafts, or transfers cancelled before any inventory left ShipHero, can be deleted.'}
                            className={`flex h-7 w-7 items-center justify-center rounded ${isDraft ? 'hover:bg-red-50' : 'cursor-not-allowed'}`}
                            onClick={(event) => { event.stopPropagation(); if (isDraft) setToDelete(row); }}
                          >
                            {deleting && toDelete?.id === row.id
                              ? <LoaderSpinner width="15" height="15" border="2" colorSpinner="red" />
                              : <Trash2 className={`h-3.5 w-3.5 ${isDraft ? 'text-red-600' : 'text-gray-300'}`} />}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfrimModal
        isOpen={canEdit && !!toDelete}
        isLoading={deleting}
        title="Are you sure you want to delete this transfer?"
        description={toDelete ? `${toDelete.transfer_number} is a Draft, so nothing has left ShipHero. Deleting it removes the draft from this list.` : ''}
        confirmButtonTitle="Delete"
        cancelClick={() => setToDelete(null)}
        submitClick={deleteDraft}
        size="max-w-lg"
      />

      <FbaTransferSidebar isOpen={sidebar.open} transferId={sidebar.id} canEdit={canEdit} onClose={closeSidebar} onChanged={load} />
    </div>
  );
};

export default FbaTransfers;
